import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Reveal, ChapterHeading } from "@/components/Reveal";
import { STATES, SERVICE_OPTIONS, API, BRAND } from "@/constants/states";

const EMPTY = { name: "", phone: "", email: "", state: "", services: [], incident_date: "", notes: "" };

const inputCls =
  "w-full bg-[#092237] border border-[#C9A24B]/25 text-[#FBF8F2] text-sm px-4 py-3.5 rounded-lg placeholder-[#8EA3B0]/60 focus:outline-none focus:border-[#C9A24B] focus:shadow-[0_0_0_1px_#C9A24B] transition-all duration-300";

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) {
      toast.error("Please select at least one service you need.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/referrals`, { ...form, incident_date: form.incident_date || null });
      toast.success("Your referral is in. A Queen will reach out within 15 minutes.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-24 py-24 sm:py-32 px-4 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <ChapterHeading
          eyebrow="05 // IMMEDIATE ADVOCACY"
          title="Connect With Our Queens Today"
          eyebrowTestId="contact-chapter-num"
          titleTestId="contact-headline"
        />
        <Reveal delay={0.1}>
          <p className="mt-5 max-w-2xl text-sm sm:text-base text-[#8EA3B0] leading-relaxed">
            Complete the intake below for immediate priority matching, or dial our line at{" "}
            <a href={BRAND.phoneHref} className="text-[#E8C87A] hover:underline">{BRAND.phone}</a>.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-8">
            <form
              data-testid="referral-form"
              onSubmit={submit}
              className="bg-[#071A2B] border border-[#C9A24B]/25 p-8 sm:p-10 shadow-[0_12px_40px_rgba(0,0,0,0.45)]"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <input data-testid="referral-input-name" required minLength={2} placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="referral-input-phone" required minLength={7} placeholder="Phone Number *" value={form.phone} onChange={set("phone")} className={inputCls} />
                <input data-testid="referral-input-email" required type="email" placeholder="Email Address *" value={form.email} onChange={set("email")} className={inputCls} />
                <select data-testid="referral-select-state" required value={form.state} onChange={set("state")} className={inputCls}>
                  <option value="">Your State *</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>{s === "Arizona" ? "Arizona (Headquarters)" : s}</option>
                  ))}
                </select>
                <input data-testid="referral-input-date" type="date" value={form.incident_date} onChange={set("incident_date")} className={`${inputCls} sm:col-span-2 [color-scheme:dark]`} aria-label="Incident date" />
              </div>

              <p className="mt-7 font-data text-[11px] tracking-[0.22em] uppercase text-[#C9A24B]">What do you need? *</p>
              <div data-testid="referral-pill-services" className="mt-3 flex flex-wrap gap-2.5">
                {SERVICE_OPTIONS.map((s) => {
                  const active = form.services.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      data-testid={`referral-pill-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => toggleService(s)}
                      className={`rounded-full px-4 py-2 text-xs font-semibold tracking-wide border transition-all duration-300 ${
                        active
                          ? "bg-[#C9A24B] text-[#04111D] border-[#C9A24B]"
                          : "bg-transparent text-[#D4DFE6] border-[#C9A24B]/30 hover:border-[#C9A24B]/70"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>

              <textarea
                data-testid="referral-input-notes"
                rows={4}
                maxLength={2000}
                placeholder="Briefly describe what happened (optional)"
                value={form.notes}
                onChange={set("notes")}
                className={`${inputCls} mt-6 resize-none`}
              />

              <button
                data-testid="referral-submit-button"
                type="submit"
                disabled={sending}
                className="mt-8 group inline-flex items-center gap-2.5 rounded-full bg-[#C9A24B] text-[#04111D] text-xs sm:text-sm uppercase tracking-[0.16em] font-bold px-8 py-4 hover:bg-[#E8C87A] hover:shadow-[0_0_32px_rgba(201,162,75,0.5)] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
                {sending ? "Dispatching..." : "Request My Resources"}
              </button>
              <p className="mt-4 text-[11px] text-[#8EA3B0]">
                100% free referral resource. Confidential. No obligation.
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-4">
            <div className="h-full flex flex-col gap-5">
              <a
                href={BRAND.phoneHref}
                data-testid="contact-phone-direct"
                className="group flex items-center gap-4 bg-[#092237] border border-[#C9A24B]/20 p-6 hover:border-[#C9A24B]/55 hover:-translate-y-0.5 transition-all duration-500"
              >
                <span className="p-3 rounded-full bg-[#C9A24B]/15 text-[#C9A24B] group-hover:bg-[#C9A24B] group-hover:text-[#04111D] transition-colors duration-300">
                  <Phone className="w-5 h-5" />
                </span>
                <span>
                  <span className="block font-data text-[10px] tracking-[0.22em] uppercase text-[#8EA3B0]">Call or Text</span>
                  <span className="block font-display font-bold text-[#FBF8F2] mt-1">{BRAND.phone}</span>
                </span>
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-direct"
                className="group flex items-center gap-4 bg-[#092237] border border-[#C9A24B]/20 p-6 hover:border-[#C9A24B]/55 hover:-translate-y-0.5 transition-all duration-500"
              >
                <span className="p-3 rounded-full bg-[#0E7C86]/20 text-[#5FC3CC] group-hover:bg-[#0E7C86] group-hover:text-[#FBF8F2] transition-colors duration-300">
                  <Mail className="w-5 h-5" />
                </span>
                <span>
                  <span className="block font-data text-[10px] tracking-[0.22em] uppercase text-[#8EA3B0]">Email</span>
                  <span className="block font-display font-bold text-[#FBF8F2] text-sm mt-1 break-all">{BRAND.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 bg-[#092237] border border-[#0E7C86]/30 p-6">
                <span className="p-3 rounded-full bg-[#0E7C86]/20 text-[#5FC3CC]">
                  <MapPin className="w-5 h-5" />
                </span>
                <span>
                  <span className="block font-data text-[10px] tracking-[0.22em] uppercase text-[#8EA3B0]">Headquarters</span>
                  <span className="block font-display font-bold text-[#FBF8F2] text-sm mt-1">Arizona &mdash; Serving 24 States</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
